import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Toolbar,
  Typography,
  CssBaseline,
  Divider,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  alpha,
  Avatar,
  Menu,
  MenuItem,
  useTheme as useMuiTheme,
  useMediaQuery,
  AppBar,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  LocalPharmacy as MedicineIcon,
  Inventory as InventoryIcon,
  Description as DescriptionIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  History as HistoryIcon,
  ListAlt as ListAltIcon,
  ChevronLeft,
  ChevronRight,
  MedicalServices,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Translate as TranslateIcon,
  FileCopy as FileCopyIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  Science as ScienceIcon,
  TrendingUp as TrendingUpIcon, // Add TrendingUpIcon for analytics
  VpnKey as VpnKeyIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme as useCustomTheme } from "../context/ThemeContext";
import { useTranslation } from "react-i18next";
import AIChatBot from "./AIChatBot";
import { getUserRoles, getMenusByRole, fetchAllMenus, settingsApi } from "../services/api";

const drawerWidth = 280;
const collapsedDrawerWidth = 70;

// Icon mapping
const iconMap = {
  DashboardIcon,
  ReceiptIcon,
  MedicineIcon,
  InventoryIcon,
  DescriptionIcon,
  PeopleIcon,
  WarningIcon,
  BusinessIcon,
  HistoryIcon,
  ListAltIcon,
  FileCopyIcon,
  NotificationsIcon,
  SearchIcon,
  ScienceIcon,
  TrendingUpIcon,
  VpnKeyIcon,
  SettingsIcon,
  SecurityIcon,
};

// Frontend menu translations mapping
const menuTranslations = {
  1: {
    en: { title: 'Dashboard & Analytics', text: 'Dashboard' },
    am: { title: 'ዳሽቦርድ እና ትንተና', text: 'ዳሽቦርድ' },
    om: { title: 'Daashboordii fi Qorannoo', text: 'Daashboordii' }
  },
  2: {
    en: { title: 'Billing & Orders', text: 'Create Order' },
    am: { title: 'ቢሊንግ እና ትዕዛዞች', text: 'ትዕዛዝ ፍጠር' },
    om: { title: 'Biliingaa fi Ajajamota', text: 'Ajajama Uumu' }
  },
  3: {
    en: { title: 'Billing & Orders', text: 'Order Management' },
    am: { title: 'ቢሊንግ እና ትዕዛዞች', text: 'የትዕዛዝ አስተዳደር' },
    om: { title: 'Biliingaa fi Ajajamota', text: 'Mangamantaa Ajajamaa' }
  },
  4: {
    en: { title: 'Medicines & Inventory', text: 'Medicine Management' },
    am: { title: 'መድሃኒቶች እና ክምችት', text: 'የመድሃኒት አስተዳደር' },
    om: { title: 'Qorichoo fi Qabeenya', text: 'Mangamantaa Qorichaa' }
  },
  5: {
    en: { title: 'Medicines & Inventory', text: 'Inventory Management' },
    am: { title: 'መድሃኒቶች እና ክምችት', text: 'የክምችት አስተዳደር' },
    om: { title: 'Qorichoo fi Qabeenya', text: 'Mangamantaa Qabeenyaa' }
  },
  6: {
    en: { title: 'Medicines & Inventory', text: 'Inventory Alerts' },
    am: { title: 'መድሃኒቶች እና ክምችት', text: 'የክምችት ማንቂያዎች' },
    om: { title: 'Qorichoo fi Qabeenya', text: 'Akeekkachiisa Qabeenyaa' }
  },
  7: {
    en: { title: 'Medicines & Inventory', text: 'Supplier Management' },
    am: { title: 'መድሃኒቶች እና ክምችት', text: 'የስብሰባ አስተዳደር' },
    om: { title: 'Qorichoo fi Qabeenya', text: 'Mangamantaa Ooggessaa' }
  },
  8: {
    en: { title: 'Prescriptions', text: 'Prescriptions' },
    am: { title: 'ፕሬስክሪፕሽን', text: 'ፕሬስክሪፕሽን' },
    om: { title: 'Qoricha Dhiheenyaa', text: 'Qoricha Dhiheenyaa' }
  },
  9: {
    en: { title: 'Prescriptions', text: 'Prescription Management' },
    am: { title: 'ፕሬስክሪፕሽን', text: 'የፕሬስክሪፕሽን አስተዳደር' },
    om: { title: 'Qoricha Dhiheenyaa', text: 'Mangamantaa Qorichaa Dhiheenyaa' }
  },
  10: {
    en: { title: 'Administration', text: 'User Management' },
    am: { title: 'አስተዳደር', text: 'የተጠቃሚ አስተዳደር' },
    om: { title: 'Administreeshinii', text: 'Mangamantaa Fayyadamtaa' }
  },
  11: {
    en: { title: 'Administration', text: 'Audit Logs' },
    am: { title: 'አስተዳደር', text: 'የኦዲት ምዝግቦች' },
    om: { title: 'Administreeshinii', text: 'Logii Auditaa' }
  },
  12: {
    en: { title: 'Reference', text: 'Directives' },
    am: { title: 'ማጣቀሻ', text: 'መመሪያዎች' },
    om: { title: 'Mallattoo', text: 'Qajeelfamota' }
  },
  13: {
    en: { title: 'Patients', text: 'Patient Management' },
    am: { title: 'የታማሚዎች አስተዳደር', text: 'የታማሚዎች አስተዳደር' },
    om: { title: 'Daawoo', text: 'Mangamantaa Daawoo' }
  },
  14: {
    en: { title: 'Patients', text: 'Notifications' },
    am: { title: 'የታማሚዎች አስተዳደር', text: 'ማስታወቂያዎች' },
    om: { title: 'Daawoo', text: 'Akeekkachiisa' }
  },
  15: {
    en: { title: 'Dashboard & Analytics', text: 'Notification Analytics' },
    am: { title: 'ዳሽቦርድ እና ትንተና', text: 'የማስታወቂያ ትንተና' },
    om: { title: 'Daashboordii fi Qorannoo', text: 'Qorannoo Akeekkachiisaa' }
  },
  16: {
    en: { title: 'Reference', text: 'Drug Reference' },
    am: { title: 'ማጣቀሻ', text: 'የመድሃኒት ማጣቀሻ' },
    om: { title: 'Mallattoo', text: 'Mallattoo Qorichaa' }
  },
  17: {
    en: { title: 'Administration', text: 'System License' },
    am: { title: 'አስተዳደር', text: 'የስርዓት ፍቃድ' },
    om: { title: 'Administreeshinii', text: 'Hayyama Sirnaa' }
  },
  18: {
    en: { title: 'Reports', text: 'Sales Reports' },
    am: { title: 'ሪፖርቶች', text: 'የሽያጭ ሪፖርቶች' },
    om: { title: 'Gabaasa', text: 'Gabaasa Gurgurtaa' }
  },
  19: {
    en: { title: 'Medicines & Inventory', text: 'Supplier Payments' },
    am: { title: 'መድሃኒቶች እና ክምችት', text: 'የአቅራቢ ክፍያዎች' },
    om: { title: 'Qorichoo fi Qabeenya', text: 'Kaffaltii Dhiyeessitootaa' }
  },
  20: {
    en: { title: 'Administration', text: 'System Settings' },
    am: { title: 'አስተዳደር', text: 'የስርዓት ቅንብሮች' },
    om: { title: 'Administreeshinii', text: 'Sirna Qindaa\'ina' }
  },
  21: {
    en: { title: 'Administration', text: 'Role & Menu Management' },
    am: { title: 'አስተዳደር', text: 'የሚና እና ምናሌ አስተዳደር' },
    om: { title: 'Administreeshinii', text: 'Mangamantaa Gahee fi Manuuwaa' }
  },
};

export default function Layout() {
  const muiTheme = useMuiTheme();
  const { mode, toggleTheme } = useCustomTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { i18n, t } = useTranslation();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  const [collapsed, setCollapsed] = useState(isMobile);
  const [menuSections, setMenuSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const [menuData, setMenuData] = useState([]); // Store raw menu data

  const toggleCollapse = () => setCollapsed(!collapsed);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLanguageMenuOpen = (event) => setLanguageAnchorEl(event.currentTarget);
  const handleLanguageMenuClose = () => setLanguageAnchorEl(null);

  const isActivePath = (path) => {
    if (location.pathname === path) return true;
    if (!location.pathname.startsWith(path + "/")) return false;

    // If it's a sub-path, only return true if there isn't a more specific menu item that also matches
    return !menuData.some(m =>
      m.path !== path &&
      location.pathname.startsWith(m.path) &&
      m.path.length > path.length
    );
  };

  // Function to get translated menu text based on current language
  const getTranslatedMenuText = (menu) => {
    // Try to use the modern i18n keys first
    const titleKey = `menu.${menu.id}.title`;
    const textKey = `menu.${menu.id}.text`;

    const translatedTitle = t(titleKey);
    const translatedText = t(textKey);

    // If translation exists (not returning the key itself)
    if (translatedTitle !== titleKey) {
      return { title: translatedTitle, text: translatedText };
    }

    // Fallback to legacy hardcoded translations if available
    const currentLanguage = i18n.language;
    if (menuTranslations[menu.id] && menuTranslations[menu.id][currentLanguage]) {
      return menuTranslations[menu.id][currentLanguage];
    }

    // Fallback to original English from the menu object
    return {
      title: menu.title || 'Untitled',
      text: menu.text || 'No Text'
    };
  };

  // Fetch menus - store raw data
  useEffect(() => {
    const loadMenus = async () => {
      if (!user) return;

      setLoading(true);
      try {
        console.log('Loading menus for language:', i18n.language);

        // Step 1: Get user roles
        const rolesRes = await getUserRoles(user.id);
        const userRoles = rolesRes.data;
        console.log('User roles:', userRoles);

        // If no roles → empty menu
        if (!userRoles?.length) {
          setMenuData([]);
          setMenuSections([]);
          setLoading(false);
          return;
        }

        // Check if user is superadmin
        const isSuperAdmin = userRoles.some(r => r.name.toLowerCase() === 'superadmin');
        console.log('Is superadmin:', isSuperAdmin);

        let menus = [];

        if (isSuperAdmin) {
          // Superadmin gets all menus
          const allMenusRes = await fetchAllMenus();
          menus = allMenusRes.data;
          console.log('Superadmin menus:', menus);
        } else {
          // Regular user: collect menus from all roles
          const menuPromises = userRoles.map(role => getMenusByRole(role.id));
          const results = await Promise.all(menuPromises);
          console.log('Menu results:', results);

          // Flatten and deduplicate by id
          const merged = results.flatMap(res => res.data || []);
          menus = Array.from(new Map(merged.map(m => [m.id, m])).values());
          console.log('Regular user menus:', menus);
        }

        // Store raw menu data for re-translation
        setMenuData(menus);

        // Apply translations to all menus
        applyTranslations(menus);

      } catch (err) {
        console.error("Failed to load menus:", err);
        setMenuData([]);
        setMenuSections([]);
      } finally {
        setLoading(false);
      }
    };

    loadMenus();
  }, [user]); // Only depend on user, not language

  // Apply translations whenever language changes
  useEffect(() => {
    if (menuData.length > 0) {
      console.log('Re-applying translations for language:', i18n.language);
      applyTranslations(menuData);
    }
  }, [i18n.language, menuData]);

  // Function to apply translations and group menus
  const applyTranslations = (menus) => {
    const translatedMenus = menus.map(menu => {
      const translated = getTranslatedMenuText(menu);
      const translatedMenu = {
        ...menu,
        displayTitle: translated.title, // Use separate field for display
        displayText: translated.text    // Use separate field for display
      };
      return translatedMenu;
    });

    // Add manual menus for superadmin that might not be in database yet
    if (user?.role === 'superadmin') {
      const branchMenuText = t('branch.management');
      const adminTitle = t('menu.administration.title') || 'Administration';

      // Check if branches menu already exists
      const hasBranches = translatedMenus.some(m => m.path === '/branches');
      if (!hasBranches) {
        translatedMenus.push({
          id: 'manual-branches',
          path: '/branches',
          displayTitle: adminTitle,
          displayText: branchMenuText,
          icon: 'BusinessIcon', // Maps to BusinessIcon in iconMap
          badge: 0
        });
      }
    }

    // Group by displayTitle
    const grouped = translatedMenus.reduce((acc, menu) => {
      let section = acc.find(s => s.title === menu.displayTitle);
      if (!section) {
        section = {
          title: menu.displayTitle,
          items: []
        };
        acc.push(section);
      }
      section.items.push(menu);
      return acc;
    }, []);

    // Stability: We remove the alphabetical sorting on translated text 
    // to prevent menu items from jumping positions when language changes.
    // The order will now follow the order returned by the API/definitions.

    setMenuSections(grouped);
  };

  const changeLanguage = (code) => {
    console.log('Changing language to:', code);
    i18n.changeLanguage(code);
    handleLanguageMenuClose();
  };

  const [companyName, setCompanyName] = useState("PharmaCare");

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await settingsApi.getCompany();
        if (response.data.success && response.data.companyInfo.name) {
          setCompanyName(response.data.companyInfo.name);
        }
      } catch (error) {
        console.error("Failed to load company settings", error);
      }
    };
    fetchCompanySettings();
  }, []);

  if (!user) {
    return (
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              background: `linear-gradient(180deg, ${muiTheme.palette.primary.main} 0%, ${muiTheme.palette.primary.dark} 100%)`,
              color: "white",
            },
          }}
        >
          <Toolbar sx={{ justifyContent: "center" }}>
            <MedicalServices sx={{ mr: 2 }} />
            <Typography variant="h6" noWrap sx={{ fontWeight: "bold" }}>
              {companyName}
            </Typography>
          </Toolbar>
          <Divider sx={{ borderColor: alpha("#fff", 0.2) }} />
          <List>
            <ListItem>
              <ListItemText
                primary="Loading..."
                sx={{ color: "white", textAlign: "center" }}
              />
            </ListItem>
          </List>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: "background.default",
            minHeight: "100vh",
            p: 3,
          }}
        >
          <Toolbar />
          <Outlet />
          <AIChatBot />
        </Box>
      </Box>
    );
  }

  const username = user.username || user.email?.split("@")[0] || "User";
  const roleNames = user.role ? [user.role] : [];

  // Supported languages WITH FLAGS
  const languages = [
    { code: "en", name: t("common.english"), flag: "🇬🇧" },
    { code: "am", name: t("common.amharic"), flag: "🇪🇹" },
    { code: "om", name: t("common.oromo"), flag: "🇪🇹" },
  ];
  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  // Debug component - optional, remove in production
  const DebugInfo = () => (
    <Box sx={{ position: 'fixed', bottom: 10, right: 10, bgcolor: 'black', color: 'white', p: 1, fontSize: '12px', zIndex: 9999, display: 'none' }}>
      Language: {i18n.language} | Menus: {menuSections.length} | Sections: {JSON.stringify(menuSections.map(s => s.title))}
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* Sidebar */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? !collapsed : true}
        onClose={() => isMobile && setCollapsed(true)}
        ModalProps={{ keepMounted: true }} // Better open performance on mobile
        sx={{
          width: isMobile ? drawerWidth : (collapsed ? collapsedDrawerWidth : drawerWidth),
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isMobile ? drawerWidth : (collapsed ? collapsedDrawerWidth : drawerWidth),
            boxSizing: "border-box",
            backgroundColor: "background.paper",
            color: "text.primary",
            borderRight: `1px solid ${alpha(muiTheme.palette.divider, 0.1)}`,
          },
        }}
      >
        {/* Header */}
        {/* Header */}
        <Toolbar
          disableGutters
          sx={{
            justifyContent: collapsed ? "center" : "space-between",
            minHeight: "80px !important",
            px: 2,
            borderBottom: `1px solid ${alpha(muiTheme.palette.divider, 0.1)}`,
            gap: 1
          }}
        >
          {(!collapsed || isMobile) && (
            <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
              <MedicalServices sx={{ mr: 1.5, color: "primary.main", fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2, wordBreak: "break-word" }}>
                  {companyName}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t("layout.managementSystem")}
                </Typography>
              </Box>
            </Box>
          )}
          {collapsed && !isMobile && (
            <MedicalServices sx={{ color: "primary.main", fontSize: 32 }} />
          )}
          <Tooltip title={collapsed ? t("layout.expandSidebar") : t("layout.collapseSidebar")}>
            <IconButton
              onClick={toggleCollapse}
              size="small"
              sx={{
                border: `1px solid ${alpha(muiTheme.palette.primary.main, 0.2)}`,
                bgcolor: alpha(muiTheme.palette.primary.main, 0.08),
                color: "primary.main",
                flexShrink: 0,
                transition: "all 0.2s",
                boxShadow: `0 2px 8px ${alpha(muiTheme.palette.primary.main, 0.15)}`,
                "&:hover": {
                  bgcolor: "primary.main",
                  color: "white",
                  transform: "scale(1.05)",
                  boxShadow: `0 4px 12px ${alpha(muiTheme.palette.primary.main, 0.3)}`,
                },
              }}
            >
              {!isMobile && (collapsed ? <ChevronRight /> : <ChevronLeft />)}
              {isMobile && <ChevronLeft />}
            </IconButton>
          </Tooltip>
        </Toolbar>

        <Divider />

        {/* Loading State */}
        {loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={24} />
            <Typography variant="body2" mt={1}>
              {t("layout.loadingMenu") || "Loading menu..."}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflow: "auto", flexGrow: 1 }}>
            {menuSections.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary" textAlign="center">
                  {t("layout.noAccess") || "No access to any pages."}
                </Typography>
              </Box>
            ) : (
              menuSections.map((section) => (
                <Box key={section.title} component="nav">
                  {!collapsed && (
                    <ListItem sx={{ py: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: "bold",
                          color: "text.secondary",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          fontSize: "0.7rem",
                        }}
                      >
                        {section.title}
                      </Typography>
                    </ListItem>
                  )}
                  <List dense>
                    {section.items.map((item) => (
                      <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => {
                            navigate(item.path);
                            if (isMobile) setCollapsed(true); // Only auto-hide on mobile devices
                          }}
                          sx={{
                            mx: 1,
                            borderRadius: 2,
                            py: 1.2,
                            backgroundColor: isActivePath(item.path)
                              ? alpha(muiTheme.palette.primary.main, 0.1)
                              : "transparent",
                            border: isActivePath(item.path)
                              ? `1px solid ${alpha(muiTheme.palette.primary.main, 0.2)}`
                              : "1px solid transparent",
                            "&:hover": {
                              backgroundColor: alpha(muiTheme.palette.primary.main, 0.05),
                              border: `1px solid ${alpha(muiTheme.palette.primary.main, 0.1)}`,
                            },
                            justifyContent: collapsed ? "center" : "flex-start",
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: collapsed ? "auto" : 40,
                              color: isActivePath(item.path)
                                ? "primary.main"
                                : "text.secondary",
                            }}
                          >
                            {React.createElement(iconMap[item.icon] || DashboardIcon)}
                          </ListItemIcon>
                          {!collapsed && (
                            <>
                              <ListItemText
                                primary={item.displayText}
                                primaryTypographyProps={{
                                  fontSize: "0.9rem",
                                  fontWeight: isActivePath(item.path) ? "bold" : "normal",
                                  color: isActivePath(item.path) ? "primary.main" : "text.primary",
                                  noWrap: true,
                                }}
                              />
                              {item.badge > 0 && (
                                <Chip
                                  label={item.badge}
                                  size="small"
                                  color="error"
                                  sx={{ height: 20, minWidth: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </>
                          )}
                        </ListItemButton>
                        {collapsed && item.badge > 0 && (
                          <Chip
                            label={item.badge}
                            size="small"
                            color="error"
                            sx={{
                              position: "absolute",
                              right: 8,
                              top: 10,
                              height: 16,
                              fontSize: "0.6rem",
                            }}
                          />
                        )}
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 1, opacity: 0.5 }} />
                </Box>
              ))
            )}
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: `1px solid ${alpha(muiTheme.palette.divider, 0.1)}` }}>

          {/* Theme Toggle Button */}
          <ListItemButton
            onClick={toggleTheme}
            sx={{
              borderRadius: 2,
              mb: 1,
              justifyContent: collapsed ? "center" : "flex-start"
            }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? "auto" : 40 }}>
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </ListItemIcon>
            {!collapsed && (
              <ListItemText primary={t("layout.themeMode")} />
            )}
          </ListItemButton>

          {/* Language Switch Button WITH FLAG */}
          <ListItemButton
            onClick={handleLanguageMenuOpen}
            sx={{
              borderRadius: 2,
              mb: 1,
              justifyContent: collapsed ? "center" : "flex-start"
            }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? "auto" : 40 }}>
              <TranslateIcon />
            </ListItemIcon>
            {!collapsed && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span role="img" aria-label={currentLang.name} style={{ fontSize: '1.2em' }}>
                  {currentLang.flag}
                </span>
                <Typography variant="body2">{currentLang.name}</Typography>
              </Box>
            )}
          </ListItemButton>

          {/* User Profile */}
          <ListItemButton onClick={handleMenuOpen} sx={{ borderRadius: 2 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "primary.main",
                fontSize: "14px",
                mr: 1,
              }}
            >
              {username.charAt(0).toUpperCase()}
            </Avatar>
            {!collapsed && (
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight="medium" noWrap>
                  {username}
                </Typography>
                <Typography variant="caption" color="textSecondary" noWrap>
                  {roleNames.join(", ")}
                </Typography>
              </Box>
            )}
          </ListItemButton>
        </Box>
      </Drawer>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ elevation: 8 }}
      >
        <MenuItem onClick={() => navigate("/change-password")}>
          <ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t("layout.changePassword")}</ListItemText>
        </MenuItem>

        <MenuItem onClick={toggleTheme}>
          <ListItemIcon>
            {mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{t("layout.themeMode")}</ListItemText>
        </MenuItem>

        <MenuItem onClick={logout}>
          <ListItemIcon><PeopleIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primary={t("layout.logout")} />
        </MenuItem>
      </Menu>

      {/* Language Menu WITH FLAGS */}
      <Menu
        anchorEl={languageAnchorEl}
        open={Boolean(languageAnchorEl)}
        onClose={handleLanguageMenuClose}
        PaperProps={{ elevation: 8 }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            selected={i18n.language === lang.code}
          >
            <ListItemIcon>
              <span role="img" aria-label={lang.name} style={{ fontSize: '1.2em' }}>
                {lang.flag}
              </span>
            </ListItemIcon>
            <ListItemText>{lang.name}</ListItemText>
            {i18n.language === lang.code && (
              <ListItemIcon>✓</ListItemIcon>
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          color: "text.primary",
          minHeight: "100vh",
          p: { xs: 2, sm: 3 },
          width: { xs: "100%", md: `calc(100% - ${collapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          overflowX: 'auto',
        }}
      >
        {isMobile && (
          <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <Toolbar>
              <IconButton color="inherit" edge="start" onClick={toggleCollapse} sx={{ mr: 2 }}>
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                {companyName}
              </Typography>
            </Toolbar>
          </AppBar>
        )}
        <Toolbar sx={isMobile ? undefined : { display: 'none' }} />
        <Outlet />
        <AIChatBot />
        <DebugInfo />
      </Box>
    </Box>
  );
}